import Phaser from 'phaser';
import { guardCancelledTouches } from './game/input/touchCancel';
import { computeGameSize } from './game/layout';
import { BootScene } from './game/scenes/BootScene';
import { LabScene } from './game/scenes/LabScene';
import { PlayScene } from './game/scenes/PlayScene';
import { TransitScene } from './game/scenes/TransitScene';
import { saveStore } from './game/storage';
import { registerOfflineSupport } from './platform/pwa';

const parent = document.getElementById('game');
if (!parent) throw new Error('#game container missing');

/** Size of the safe-area-padded container the canvas must fit in. */
function measure() {
  return computeGameSize(parent!.clientWidth, parent!.clientHeight);
}

// iOS home swipes cancel the touch: never let that press a button.
guardCancelledTouches();

const initial = measure();
const game = new Phaser.Game({
  type: Phaser.AUTO,
  parent,
  backgroundColor: '#0d0a1f',
  pixelArt: true,
  roundPixels: true,
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
    width: initial.width,
    height: initial.height,
  },
  input: { activePointers: 4 },
  scene: [BootScene, LabScene, TransitScene, PlayScene],
});

// Dev builds expose the game for browser automation (tools/) and console poking.
if (import.meta.env.DEV) {
  window.__bantam = {
    game,
    play: () => game.scene.getScene('Play') as PlayScene,
    transit: () => game.scene.getScene('Transit') as TransitScene,
    lab: () => game.scene.getScene('Lab') as LabScene,
    save: () => saveStore(),
  };
}

// Offline play; an update waits until no run is in progress (the lab is between runs).
registerOfflineSupport(() => game.scene.isActive('Play') || game.scene.isActive('Transit'));

// Keep the internal width matched to the screen's shape: rotation, browser chrome, safe areas.
// Only once the game has booted: the scale manager can't resize before then (a ResizeObserver
// fires right away, which broke the classic-script build used for artifacts).
const fit = () => {
  const next = measure();
  if (next.width !== game.scale.gameSize.width) {
    game.scale.setGameSize(next.width, next.height);
  }
  game.scale.refresh();
};
game.events.once(Phaser.Core.Events.READY, () => {
  window.addEventListener('resize', fit);
  new ResizeObserver(fit).observe(parent);
  fit();
});
